import { ActionTree, ActionContext } from 'vuex';
import { setUser } from '@sentry/browser';

import i18next, { setLocale } from '@/i18n';
import api from '@/api';

import { ProfileState, ProfileMutations, ProfileActions, UserRole, Ad } from '@/store/modules/user/profile/types';
import { RootState, EventTypes, RootActions, ListStatus, EditableStatus } from '@/store/types';
import { NotificationsActions } from '@/store/modules/notifications/types';
import { UserActions, UserMutations } from '@/store/modules/user/types';
import { Profile, ProfileJSON } from './profile';
import blogApi from '@/store/utils/blog-api';
import { AdJSON } from '@/store/modules/ads/ad';
import config from '@/config';
import { checkForNewProducts, initializeProductStorage, getStoredProductIds, getNewProductIds } from '@/utils/product-notifications';

export const actions: ActionTree<ProfileState, RootState> = {
  [ProfileActions.LOAD_PROFILE]({ commit, getters, dispatch }): Promise<void> {
    if (getters.isLoading) {
      return Promise.resolve();
    }

    commit(ProfileMutations.SET_CURRENT_PROFILE_STATUS, ListStatus.Loading);
    commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Loading);

    return api.auth
      .me()
      .then(async (response) => {
        const profile = Profile.fromJSON(response.data);

        setUser({
          email: profile.email,
          id: String(profile.id),
          extra: {
            isAdmin: profile.role === UserRole.SuperAdmin,
          },
        });

        await setLocale(profile.languageCode);

        return dispatch(ProfileActions.SET_PROFILE, profile);
      })
      .then(() => {
        commit(ProfileMutations.SET_CURRENT_PROFILE_STATUS, ListStatus.Loaded);
        commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Loaded);
        return dispatch(RootActions.DISPATCH_EVENT, { type: EventTypes.SESSION }, { root: true });
      })
      .then(() => {
        // Check for new products after profile is loaded
        return dispatch(ProfileActions.CHECK_NEW_PRODUCTS);
      })
      .catch((error) => {
        commit(ProfileMutations.SET_CURRENT_PROFILE_STATUS, ListStatus.Failed);
        commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [ProfileActions.SAVE_PROFILE]({ state, commit, getters, dispatch }) {
    if (getters.isSaving || !state.editableProfile) {
      return Promise.resolve();
    }

    const { first_name, last_name, email, image_id, password, language_code } = state.editableProfile.toJSON('server');

    commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Saving);
    return api.auth
      .updateMe({ first_name, last_name, email, image_id, password, language_code })
      .then(async (response) => {
        const profile = Profile.fromJSON(response.data);
        await dispatch(RootActions.LOAD_LANGUAGES, { force: true }, { root: true });
        await setLocale(profile.languageCode);

        setUser({
          email: profile.email,
          id: String(profile.id),
          extra: {
            isAdmin: profile.role === UserRole.SuperAdmin,
          },
        });

        blogApi.setBlogLang(profile);

        return dispatch(ProfileActions.SET_PROFILE, profile);
      })
      .then(() => {
        commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Saved);

        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('Your profile was successfully updated.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        commit(ProfileMutations.SET_EDITABLE_PROFILE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [ProfileActions.UPDATE_EDITABLE_PROFILE]({ commit }, profile: Partial<ProfileJSON>): void {
    commit(ProfileMutations.UPDATE_EDITABLE_PROFILE, profile);
  },
  async [ProfileActions.SET_PROFILE]({ commit }, profile: Profile) {
    commit(ProfileMutations.SET_CURRENT_PROFILE, profile);
    commit(ProfileMutations.SET_EDITABLE_PROFILE, profile.clone());
  },
  [ProfileActions.SAVE_TERMS_AGREE]({ state, getters, commit, dispatch }, termsAgree: boolean): Promise<void> {
    if (!getters.isLoaded) {
      return Promise.resolve();
    }

    const newValue = termsAgree ? 1 : 0;
    const shouldUpdateAPI = newValue !== state.currentProfile.termsAgree;

    commit(ProfileMutations.SET_TERMS_AGREE, newValue);

    if (!shouldUpdateAPI) {
      if (!newValue) {
        return dispatch('user/' + UserActions.LOGOUT, undefined, {
          root: true,
        });
      }

      return Promise.resolve();
    }

    // Show spinner, because we are waiting for profile update request
    if (!newValue) {
      window.loader(true);
      commit('user/' + UserMutations.LOGOUT_PENDING, undefined, { root: true });
    }

    return api.auth
      .updateMe({ terms_agree: newValue })
      .then((response) => {
        const profile = Profile.fromJSON(response.data);

        setUser({
          email: profile.email,
          id: String(profile.id),
          extra: {
            isAdmin: profile.role === UserRole.SuperAdmin,
          },
        });

        return dispatch(ProfileActions.SET_PROFILE, profile);
      })
      .then(() => {
        if (!newValue) {
          return dispatch('user/' + UserActions.LOGOUT, undefined, {
            root: true,
          });
        }

        commit(ProfileMutations.SET_TERMS_AGREE, newValue);
      })
      .catch((error) => {
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  async [ProfileActions.SHOW_TERMS_AGREE_MODAL]({ commit }) {
    commit(ProfileMutations.SHOW_TERMS_AGREE_MODAL);
  },
  [ProfileActions.SAVE_DEVICE_SECURITY_AGREE]({ state, getters, commit, dispatch }, deviceSecurityAgree: boolean): Promise<void> {
    if (!getters.isLoaded) {
      return Promise.resolve();
    }

    const newValue = deviceSecurityAgree ? 1 : 0;
    const shouldUpdateAPI = newValue !== state.currentProfile.deviceSecurityAgree;

    commit(ProfileMutations.SET_DEVICE_SECURITY_AGREE, newValue);

    if (!shouldUpdateAPI) {
      // Update localStorage to mark current products as seen (for new product notifications)
      const practice = getters.currentProfile?.practice;
      if (practice?.products) {
        checkForNewProducts(practice.products);
      }
      
      // Mark new products as dismissed to hide the modal
      commit(ProfileMutations.SET_NEW_PRODUCTS_DISMISSED, true);
      return Promise.resolve();
    }

    return api.auth
      .updateMe({ device_security_agree: newValue })
      .then((response) => {
        const profile = Profile.fromJSON(response.data);

        setUser({
          email: profile.email,
          id: String(profile.id),
          extra: {
            isAdmin: profile.role === UserRole.SuperAdmin,
          },
        });

        return dispatch(ProfileActions.SET_PROFILE, profile);
      })
      .then(() => {
        commit(ProfileMutations.SET_DEVICE_SECURITY_AGREE, newValue);
        
        // Update localStorage to mark current products as seen (for new product notifications)
        const practice = getters.currentProfile?.practice;
        if (practice?.products) {
          checkForNewProducts(practice.products);
        }
        
        // Mark new products as dismissed to hide the modal
        commit(ProfileMutations.SET_NEW_PRODUCTS_DISMISSED, true);
      })
      .catch((error) => {
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [ProfileActions.SET_LANGUAGE]({ state, commit }: ActionContext<ProfileState, RootState>, language: string) {
    commit(ProfileMutations.SET_CURRENT_PROFILE, { ...state.currentProfile, language });
  },
  [ProfileActions.ENABLE_CHATBOT]({ commit }: ActionContext<ProfileState, RootState>) {
    commit(ProfileMutations.SET_SHOULD_LOAD_CHATBOT, true);
  },
  [ProfileActions.FETCH_ADS]({ commit, state, getters, dispatch }: ActionContext<ProfileState, RootState>) {
    if (state.ads.length) {
      return Promise.resolve();
    }

    // Get dismissed ad expiry from localStorage
    const dismissedUntil = localStorage.getItem('ad-dismissed');
    if (dismissedUntil) {
      const expiryDate = new Date(dismissedUntil);
      if (new Date() < expiryDate) {
        // Ad is dismissed, enable chatbot
        return dispatch(ProfileActions.ENABLE_CHATBOT);
      } else {
        localStorage.removeItem('ad-dismissed');
      }
    }
    
    // Fetch active ads from API
    return api.ads.active({
      countries: getters.countries?.map(c => c.id),
      products: getters.products
        ?.filter(product => product.owned)
        .map(product => String(product.id))
    })
      .then((response) => {
        // Also accept the old single-ad response during a staggered deployment.
        const adJsons: AdJSON[] = Array.isArray(response.data)
          ? response.data
          : response.data ? [response.data] : [];
        const ads: Ad[] = adJsons.map((adJson) => {
          const image = Array.isArray(adJson.image) ? adJson.image[0] : adJson.image;
          return {
            id: adJson.id,
            name: adJson.name,
            image: image?.thumbnail || '',
            link: adJson.url,
          };
        }).filter((ad) => ad.image && ad.link);

        commit(ProfileMutations.SET_ADS, ads);
        if (!ads.length) {
          return dispatch(ProfileActions.ENABLE_CHATBOT);
        }
      })
      .catch((error) => {
        // If there was an error fetching ads, enable chatbot
        dispatch(ProfileActions.ENABLE_CHATBOT);
      });
  },
  [ProfileActions.DISMISS_AD]({ commit, dispatch }: ActionContext<ProfileState, RootState>) {
    // Set dismissal expiry in localStorage
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 3);
    localStorage.setItem('ad-dismissed', expiryDate.toISOString());
    
    // Dismiss the whole carousel for the existing three-day period.
    commit(ProfileMutations.SET_AD_DISMISSED);
    
    // Enable chatbot
    return dispatch(ProfileActions.ENABLE_CHATBOT);
  },
  [ProfileActions.MINIMIZE_AD]({ commit }: ActionContext<ProfileState, RootState>) {
    commit(ProfileMutations.SET_AD_MINIMIZED, true);
  },
  [ProfileActions.EXPAND_AD]({ commit }: ActionContext<ProfileState, RootState>) {
    commit(ProfileMutations.SET_AD_MINIMIZED, false);
  },
  [ProfileActions.CHECK_NEW_PRODUCTS]({ getters, commit }): Promise<void> {
    const practice = getters.currentProfile?.practice;
    if (!practice?.products) {
      return Promise.resolve();
    }
    const currentProducts = practice.products;
    const storedIds = getStoredProductIds();
    // If user has no stored products, initialize storage but don't show modal
    if (storedIds.length === 0) {
      initializeProductStorage(currentProducts);
      return Promise.resolve();
    }
    // Check for new products (don't update storage yet - that happens when modal is dismissed)
    const newProductIds = getNewProductIds(currentProducts);
    
    // Only reset dismissed state if there are actually new products
    // (not just the same products that were already seen)
    if (newProductIds.length > 0) {
      commit(ProfileMutations.SET_NEW_PRODUCTS_DISMISSED, false);
    }

    return Promise.resolve();
  },
  [ProfileActions.OPEN_CLINICAL_EVENTS_MODAL]({ commit }) {
    commit(ProfileMutations.SET_SHOW_CLINICAL_EVENTS_MODAL, true);
  },
  [ProfileActions.CLOSE_CLINICAL_EVENTS_MODAL]({ commit }) {
    commit(ProfileMutations.SET_SHOW_CLINICAL_EVENTS_MODAL, false);
  },
};

export default actions;
